import React, { useId, useMemo, useState } from "react"

import {
  Combobox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type ComboboxOption,
} from "@coval/ui"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { TraceFilterConfig } from "@/types/metrics/metadata"

import {
  useCustomTraceMetricPreviewTransport,
  useTraceFilterSuggestionsTransport,
} from "../routes/useMetricConfigurationTransport"
import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"
import { TraceFilterSection } from "../trace-filter"
import {
  CUSTOM_TRACE_UNIT_OPTIONS,
  FALLBACK_ATTRIBUTES,
  isAggregationMethod,
  KNOWN_ATTRIBUTES_BY_SPAN,
  KNOWN_SPAN_NAMES,
  requiresNumericAttribute,
  resolveCustomTraceUnit,
  type AggregationMethod,
  type CustomTraceUnitOptionKey,
} from "../utils/traceConstants"
import {
  MAX_TRACE_AGENT_SCOPE_IDS,
  toTraceAgentIds,
} from "./customTraceAgentScope"
import {
  getCustomTraceAggregationOptions,
  getCustomTraceAggregationWarning,
  getCustomTracePreviewEvidence,
} from "./customTraceAggregation"
import { CustomTraceAggregationField } from "./CustomTraceAggregationField"
import {
  getImpliedUnit,
  inferUnitKey,
  MAX_CUSTOM_UNIT_LENGTH,
  mergeSuggestionOptions,
  withoutMetricAttribute,
  withoutUnitMetadata,
} from "./customTraceMetadata"
import { CustomTracePreview } from "./CustomTracePreview"
import {
  CustomTraceValueSourceField,
  type CustomTraceValueSource,
} from "./CustomTraceValueSourceField"
import { RequiredMarker } from "./RequiredMarker"

function toComboboxOption(suggestion: {
  key: string
  label: string
  description?: string
}): ComboboxOption {
  return {
    value: suggestion.key,
    label: suggestion.label,
    description: suggestion.description,
  }
}

interface CustomTraceConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
  selectedAgentIds?: string[]
  supportsValues?: boolean
}

export const CustomTraceConfig: React.FC<CustomTraceConfigProps> = ({
  metricRecordData,
  onChange,
  selectedAgentIds = [],
  supportsValues = false,
}) => {
  const baseId = useId()
  const spanNameId = `${baseId}-span-name`
  const metricAttributeId = `${baseId}-metric-attribute`
  const unitId = `${baseId}-unit`
  const metricMetadata = useMemo(
    () => metricRecordData.metric_metadata ?? {},
    [metricRecordData.metric_metadata],
  )
  const selectedAgentScopeIds = useMemo(
    () => Array.from(new Set(selectedAgentIds)).sort(),
    [selectedAgentIds],
  )
  const traceAgentIds = useMemo(
    () => toTraceAgentIds(selectedAgentScopeIds),
    [selectedAgentScopeIds],
  )
  const hasTooManyTraceAgentIds =
    selectedAgentScopeIds.length > MAX_TRACE_AGENT_SCOPE_IDS
  const traceScopeInput = useMemo(
    () => (traceAgentIds ? { agent_ids: traceAgentIds } : undefined),
    [traceAgentIds],
  )
  const isAgentScoped = selectedAgentScopeIds.length > 0
  const currentSpanName = metricMetadata.span_name || ""
  const normalizedSpanName = currentSpanName.trim()
  const currentMetricAttribute = metricMetadata.metric_attribute ?? ""
  const rawAggregationMethod = metricMetadata.aggregation_method ?? ""
  const currentAggregationMethod: AggregationMethod = isAggregationMethod(
    rawAggregationMethod,
  )
    ? rawAggregationMethod
    : "average"
  const hasValuesSupport =
    currentAggregationMethod === "values" || supportsValues
  const hasInvalidAggregationMethod =
    rawAggregationMethod.length > 0 &&
    !isAggregationMethod(rawAggregationMethod)
  const currentValueSource: CustomTraceValueSource =
    metricMetadata.value_source === "duration" ? "duration" : "attribute"
  const isDuration = currentValueSource === "duration"
  const needsNumericAttribute = requiresNumericAttribute(
    currentAggregationMethod,
  )
  const needsAttribute =
    needsNumericAttribute || currentAggregationMethod === "values"
  const hasMetricAttribute = currentMetricAttribute.trim().length > 0

  const spanSuggestionsQuery = useTraceFilterSuggestionsTransport({
    request: traceScopeInput ?? {},
    enabled: !hasTooManyTraceAgentIds,
  })
  const attributeSuggestionsQuery = useTraceFilterSuggestionsTransport({
    request: {
      ...(traceScopeInput ?? {}),
      span_name: normalizedSpanName || undefined,
    },
    enabled: normalizedSpanName.length > 0 && !hasTooManyTraceAgentIds,
  })
  const previewQuery = useCustomTraceMetricPreviewTransport({
    request: {
      span_name: currentSpanName,
      ...(traceScopeInput ?? {}),
      ...(isDuration
        ? {
            metric_attribute: currentMetricAttribute,
            value_source: currentValueSource,
          }
        : hasMetricAttribute
          ? { metric_attribute: currentMetricAttribute }
          : {}),
    },
    enabled: normalizedSpanName.length > 0 && !hasTooManyTraceAgentIds,
  })

  const spanSuggestions = useMemo(
    () =>
      mergeSuggestionOptions(
        spanSuggestionsQuery.data?.span_names,
        isAgentScoped ? [] : KNOWN_SPAN_NAMES,
      ),
    [isAgentScoped, spanSuggestionsQuery.data?.span_names],
  )

  const mergedAttributeSuggestions = useMemo(() => {
    const fallbackAttributeSuggestions = isAgentScoped
      ? []
      : (KNOWN_ATTRIBUTES_BY_SPAN[normalizedSpanName] ?? FALLBACK_ATTRIBUTES)
    return mergeSuggestionOptions(
      attributeSuggestionsQuery.data?.attribute_keys,
      fallbackAttributeSuggestions,
    )
  }, [
    attributeSuggestionsQuery.data?.attribute_keys,
    isAgentScoped,
    normalizedSpanName,
  ])

  const spanOptions = useMemo(
    () => spanSuggestions.map(toComboboxOption),
    [spanSuggestions],
  )
  const attributeOptions = useMemo(
    () => mergedAttributeSuggestions.map(toComboboxOption),
    [mergedAttributeSuggestions],
  )

  const metadataUnit = resolveCustomTraceUnit(metricMetadata)
  const hasExplicitUnit = Object.hasOwn(metricMetadata, "unit")
  const impliedUnit = getImpliedUnit(currentAggregationMethod)
  const [selectedUnitKey, setSelectedUnitKey] =
    useState<CustomTraceUnitOptionKey>(() =>
      inferUnitKey(metadataUnit, hasExplicitUnit),
    )
  const lockedUnitKey: CustomTraceUnitOptionKey | undefined = isDuration
    ? "s"
    : impliedUnit
  const currentUnitKey = lockedUnitKey ?? selectedUnitKey
  const isUnitLocked = Boolean(lockedUnitKey)
  const customUnitValue =
    currentUnitKey === "custom" && metadataUnit ? metadataUnit : ""
  const preview = previewQuery.data
  const previewIsPending = previewQuery.isLoading || previewQuery.isFetching
  const hasResolvedPreview =
    Boolean(preview) && !previewIsPending && !previewQuery.isError
  const selectedAttributeIsBoolean =
    hasMetricAttribute &&
    hasResolvedPreview &&
    Boolean(preview?.isBoolean) &&
    (preview?.matchedSpanCount ?? 0) > 0
  const hasSpanStatusRateSupport =
    !isDuration &&
    !hasMetricAttribute &&
    hasResolvedPreview &&
    (preview?.okStatusCount ?? 0) + (preview?.errorStatusCount ?? 0) > 0
  const hasRateSupport = selectedAttributeIsBoolean || hasSpanStatusRateSupport
  const aggregationOptions = useMemo(
    () =>
      getCustomTraceAggregationOptions({
        isDuration,
        hasMetricAttribute,
        hasRateSupport,
        hasValuesSupport,
      }),
    [hasMetricAttribute, hasRateSupport, hasValuesSupport, isDuration],
  )
  const aggregationWarning = getCustomTraceAggregationWarning({
    method: currentAggregationMethod,
    options: aggregationOptions,
    isDuration,
    metricAttribute: currentMetricAttribute.trim(),
    spanName: normalizedSpanName,
    evidence: getCustomTracePreviewEvidence({
      hasTooManyAgents: hasTooManyTraceAgentIds,
      isPending: previewIsPending,
      isError: previewQuery.isError,
      matchedSpanCount: preview?.matchedSpanCount,
      isNumeric: preview?.isNumeric,
    }),
  })
  // The saved aggregation and unit are always shown as saved. When the
  // preview can't back them up, the field warns instead of rewriting them.
  const shouldShowAggregationMethod =
    normalizedSpanName.length > 0 || isDuration
  const shouldShowUnitField =
    shouldShowAggregationMethod && needsNumericAttribute

  const updateMetadata = (metadata: MetricRecordData["metric_metadata"]) => {
    const aggregationMethod = metadata.aggregation_method ?? ""
    const normalizedMetadata =
      hasInvalidAggregationMethod && !isAggregationMethod(aggregationMethod)
        ? {
            ...metadata,
            aggregation_method: currentAggregationMethod,
          }
        : metadata
    const metadataForUpdate =
      normalizedMetadata.metric_attribute?.trim() ||
      normalizedMetadata.value_source === "duration"
        ? normalizedMetadata
        : withoutMetricAttribute(normalizedMetadata)

    onChange({
      ...metricRecordData,
      metric_metadata: metadataForUpdate,
      output_type:
        metadataForUpdate.aggregation_method === "values" ? "set" : "float",
    })
  }

  const handleSpanNameChange = (value: string) => {
    updateMetadata({
      ...metricMetadata,
      span_name: value,
    })
  }

  const handleTraceFilterChange = (
    nextValue: TraceFilterConfig | undefined,
  ) => {
    if (nextValue === undefined) {
      const { trace_filter: _ignored, ...rest } = metricMetadata
      updateMetadata(rest)
      return
    }
    updateMetadata({
      ...metricMetadata,
      trace_filter: nextValue,
    })
  }

  const handleMetricAttributeChange = (value: string) => {
    updateMetadata({
      ...metricMetadata,
      metric_attribute: value,
    })
  }

  const handleAggregationMethodChange = (method: AggregationMethod) => {
    const nextImpliedUnit = getImpliedUnit(method)
    const nextMetadata = {
      ...metricMetadata,
      aggregation_method: method,
    }
    const shouldClearAutoUnit =
      (impliedUnit && metadataUnit === impliedUnit) ||
      (nextImpliedUnit && metadataUnit === nextImpliedUnit)
    const shouldClearUnitMetadata =
      shouldClearAutoUnit || !requiresNumericAttribute(method)
    const metadataForUpdate = shouldClearUnitMetadata
      ? withoutUnitMetadata(nextMetadata)
      : nextMetadata

    if (!nextImpliedUnit) {
      setSelectedUnitKey(
        inferUnitKey(
          resolveCustomTraceUnit(metadataForUpdate),
          Object.hasOwn(metadataForUpdate, "unit"),
        ),
      )
    }
    updateMetadata(metadataForUpdate)
  }

  const handleValueSourceChange = (value: CustomTraceValueSource) => {
    if (value === "duration") {
      setSelectedUnitKey("s")
      updateMetadata({
        ...metricMetadata,
        value_source: "duration",
        metric_attribute: "",
        unit: "s",
        units: "s",
        unit_abbreviation: "s",
      })
      return
    }
    const { value_source: _valueSource, ...metadataWithoutValueSource } =
      metricMetadata
    const restoredMetadata = withoutUnitMetadata(metadataWithoutValueSource)
    setSelectedUnitKey(
      inferUnitKey(
        resolveCustomTraceUnit(restoredMetadata),
        Object.hasOwn(restoredMetadata, "unit"),
      ),
    )
    updateMetadata(restoredMetadata)
  }

  const handleUnitChange = (unitKey: CustomTraceUnitOptionKey) => {
    const option = CUSTOM_TRACE_UNIT_OPTIONS.find(({ key }) => key === unitKey)
    const unit = option?.metadataUnit
    if (unitKey === "none") {
      setSelectedUnitKey("none")
      updateMetadata(withoutUnitMetadata(metricMetadata))
      return
    }
    if (unitKey === "custom") {
      setSelectedUnitKey("custom")
      updateMetadata({
        ...metricMetadata,
        unit: "",
        units: "",
        unit_abbreviation: "",
      })
      return
    }
    setSelectedUnitKey(unitKey)
    updateMetadata({
      ...metricMetadata,
      unit,
      units: unit,
      unit_abbreviation: unit,
    })
  }

  const handleCustomUnitChange = (value: string) => {
    const unit = value.slice(0, MAX_CUSTOM_UNIT_LENGTH)
    setSelectedUnitKey("custom")
    updateMetadata({
      ...metricMetadata,
      unit,
      units: unit,
      unit_abbreviation: unit,
    })
  }

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label
            htmlFor={spanNameId}
            id={`${spanNameId}-label`}
            className={LABEL_STYLES}
          >
            Span Name
            <RequiredMarker />
          </label>
        </div>
        <Combobox
          id={spanNameId}
          inputValue={currentSpanName}
          onInputChange={handleSpanNameChange}
          options={spanOptions}
          placeholder="e.g. llm, tts, stt"
          aria-labelledby={`${spanNameId}-label`}
        />
      </div>

      {/* Keyed by metric: the viewer swaps records in place, so a pending
          duration confirmation must not carry over to the next metric. */}
      <CustomTraceValueSourceField
        key={metricRecordData.id}
        value={currentValueSource}
        metricAttribute={currentMetricAttribute}
        onChange={handleValueSourceChange}
      />

      {!isDuration && normalizedSpanName && (
        <div className={FIELD_CONTAINER_STYLES}>
          <div className={LABEL_ROW_STYLES}>
            <label
              htmlFor={metricAttributeId}
              id={`${metricAttributeId}-label`}
              className={LABEL_STYLES}
            >
              Metric Attribute
              {needsAttribute && <RequiredMarker />}
            </label>
          </div>
          <Combobox
            id={metricAttributeId}
            inputValue={currentMetricAttribute}
            onInputChange={handleMetricAttributeChange}
            options={attributeOptions}
            placeholder={
              needsAttribute
                ? "e.g. metrics.ttfb"
                : "Optional; e.g. metrics.ttfb, stt.providerName"
            }
            aria-labelledby={`${metricAttributeId}-label`}
          />
        </div>
      )}

      {shouldShowAggregationMethod && (
        <CustomTraceAggregationField
          options={aggregationOptions}
          value={currentAggregationMethod}
          warning={aggregationWarning}
          onChange={handleAggregationMethodChange}
        />
      )}

      {shouldShowUnitField && (
        <div className={FIELD_CONTAINER_STYLES}>
          <div className={LABEL_ROW_STYLES}>
            <label
              htmlFor={unitId}
              id={`${unitId}-label`}
              className={LABEL_STYLES}
            >
              Unit
            </label>
          </div>
          <Select
            value={currentUnitKey}
            onValueChange={(value) =>
              handleUnitChange(value as CustomTraceUnitOptionKey)
            }
            disabled={isUnitLocked}
          >
            <SelectTrigger id={unitId} aria-labelledby={`${unitId}-label`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CUSTOM_TRACE_UNIT_OPTIONS.map((unit) => (
                <SelectItem key={unit.key} value={unit.key}>
                  {unit.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentUnitKey === "custom" && !isUnitLocked && (
            <Input
              aria-label="Custom unit text"
              className="mt-2"
              maxLength={MAX_CUSTOM_UNIT_LENGTH}
              value={customUnitValue}
              onChange={(event) => handleCustomUnitChange(event.target.value)}
            />
          )}
        </div>
      )}

      <CustomTracePreview
        currentSpanName={currentSpanName}
        hasMetricAttribute={hasMetricAttribute}
        hasTooManyTraceAgentIds={hasTooManyTraceAgentIds}
        isDuration={isDuration}
        preview={preview}
        previewIsError={previewQuery.isError}
        previewIsPending={previewIsPending}
        previewRequiresNumericAttribute={needsNumericAttribute}
        aggregationMethod={currentAggregationMethod}
      />

      <TraceFilterSection
        value={metricMetadata.trace_filter}
        onChange={handleTraceFilterChange}
      />
    </div>
  )
}
